import React, { useMemo, useState } from 'react'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { dissoc, pick } from 'ramda'
import { routeManager } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { listVolumeTypes, deleteVolumeType } from './actions'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { volumeTypesSelector } from './selectors'
import InferActionParams from 'core/actions/InferActionParams'
import { useAppSelector } from 'app/store'
import { durationBetweenDates } from 'utils/misc'
import CreateVolumeTypeModal from './CreateVolumeTypeModal'
import EditVolumeTypeModal from './EditVolumeTypeModal'
import EditVolumeTypeTenantsModal from './EditVolumeTypeTenantsModal'
// import SnapshotVolumeDialog from './SnapshotVolumeDialog'
// import AttachVolumeDialog from './AttachVolumeDialog'
// import DetachVolumeDialog from './DetachVolumeDialog'
import { humanReadableSize } from 'openstack/helpers'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import DeleteVolumeTypeDialog from './DeleteVolumeTypeDialog'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { preventDeleteDefaultText } from './constant'
import { IVolumeTypeDetailsPageTabs } from './details/model'

type ModelDataKey = DataKeys.VolumeTypes
type SelectorModel = ArrayElement<ReturnType<typeof volumeTypesSelector>>
type ActionParams = InferActionParams<typeof listVolumeTypes>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'VolumeTypes',
  listTablePrefs,
)

const searchTargets = ['name', 'id', 'description']

const volumeTypeColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    // width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id, name }) =>
        !!name
          ? routeManager.routes.openstack.volumeTypeDetails.path({
              id,
              tab: IVolumeTypeDetailsPageTabs.Overview,
            })
          : '',
    }),
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'extra_specs.volume_backend_name',
    label: 'Volume Backend',
  },
  {
    key: 'extra_specs',
    label: 'Metadata',
    disableSorting: true,
    CellComponent: ({ value }) =>
      createResourceLabelsCell({ type: 'table', separator: '=' })({
        value: dissoc('volume_backend_name', value),
      }),
  },
  {
    key: 'is_public',
    label: 'Public',
    render: (val) => (val ? 'True' : 'False'),
  },
  // {
  //   key: 'status',
  //   label: 'Status',
  // },
  // {
  //   key: 'volume_type',
  //   label: 'Type',
  // },
  // {
  //   key: 'attachedVm',
  //   label: 'VM',
  //   render: (vm) => vm?.name,
  // },
  // {
  //   key: 'device',
  //   label: 'Device',
  // },
  // {
  //   key: 'size',
  //   label: 'Capacity',
  //   render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  // },
  // {
  //   key: 'bootable',
  //   label: 'Bootable',
  // },
  // {
  //   key: 'created_at',
  //   label: 'Age',
  //   formatFn: (value) => durationBetweenDates({ labels: ['d'] })(value),
  // },
]

export default function VolumeTypesListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listVolumeTypes, {
    params,
  })
  const data = useAppSelector(volumeTypesSelector)

  const filteredVolumeTypeLists = useMemo(() => {
    if (data?.length > 0) {
      return data.filter((type) => type.name !== '__DEFAULT__')
    }
    return []
  }, [data])

  const [selectedVolumeType, setSelectedVolumeType] = useState(null)
  const [showDeleteVolumeTypeDialog, setShowDeleteVolumeTypeDialog] = useState(null)
  const [showEditVolumeTypeModal, setShowEditVolumeTypeModal] = useState(false)
  const [showEditVolumeTypeTenantsModal, setShowEditVolumeTypeTenantsModal] = useState(false)
  // const [showAttachVolumeDialog, setShowAttachVolumeDialog] = useState(false)
  // const [showDetachVolumeDialog, setShowDetachVolumeDialog] = useState(false)

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (volumeTypes) => {
        return volumeTypes?.length === 1
      },
      disabledTooltip: 'You can only edit a single volume type at a time',
      icon: 'edit',
      label: 'Edit',
      handleAction: (volumeTypes) => {
        setSelectedVolumeType(volumeTypes[0] ?? null)
        setShowEditVolumeTypeModal(true)
      },
    },
    {
      cond: (volumeTypes) => {
        return volumeTypes?.length === 1 && !volumeTypes?.[0]?.is_public
      },
      disabledTooltip: 'You can only edit tenants for private volume types',
      icon: 'users',
      label: 'Edit Tenants',
      handleAction: (volumeTypes) => {
        setSelectedVolumeType(volumeTypes[0] ?? null)
        setShowEditVolumeTypeTenantsModal(true)
      },
    },
  ]

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Edit',
      icon: 'edit',
      handleClick: (volumeType) => {
        setSelectedVolumeType(volumeType)
        setShowEditVolumeTypeModal(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: false,
    },
    {
      label: 'Edit Tenants',
      icon: 'users',
      disabledTooltip: 'You can only edit tenants for private volume types',
      cond: (volumeTypes) => volumeTypes && !volumeTypes?.is_public,
      handleClick: (volumeType) => {
        setSelectedVolumeType(volumeType)
        setShowEditVolumeTypeTenantsModal(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: false,
    },
    {
      label: 'Delete',
      icon: 'trash-alt',
      cond: (volumeType) => volumeType?.name !== preventDeleteDefaultText,
      disabledTooltip: 'Cannot delete default volume type',
      handleClick: (volumeType) => {
        setSelectedVolumeType(volumeType)
        setShowDeleteVolumeTypeDialog(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: false,
    },
  ]

  return (
    <>
      <DocumentMeta title="Volume Types" />
      <CreateVolumeTypeModal addRoute={routeManager.routes.openstack.createVolumeType} />
      {showEditVolumeTypeModal && (
        <EditVolumeTypeModal
          rows={[selectedVolumeType]}
          onClose={() => setShowEditVolumeTypeModal(false)}
        />
      )}
      {showEditVolumeTypeTenantsModal && (
        <EditVolumeTypeTenantsModal
          rows={[selectedVolumeType]}
          onClose={() => setShowEditVolumeTypeTenantsModal(false)}
        />
      )}
      {showDeleteVolumeTypeDialog && (
        <DeleteVolumeTypeDialog
          rows={[selectedVolumeType]}
          onClose={() => setShowDeleteVolumeTypeDialog(false)}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.VolumeTypes}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={filteredVolumeTypeLists}
        columns={volumeTypeColumns}
        addUrl={routeManager.routes.openstack.createVolumeType.path()}
        addText="Create New Volume Type"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteVolumeType}
        deleteCond={(selectedItems) =>
          !selectedItems?.some((item) => item?.name === preventDeleteDefaultText)
        }
        deleteDisabledInfo="Cannot delete default volume type"
        DeleteDialogComponent={DeleteVolumeTypeDialog}
        batchActions={batchActions}
        rowMenuItems={rowMenuItems}
        isReadOnly={!isAdmin()}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
